/**
 * Handlers SSE específicos de la entidad Edition.
 * Registra los eventos relacionados con el workflow de documentación.
 */
import { RootStore } from '../../store';

/**
 * Handler para cuando la documentación está lista.
 */
function handleEditionDocumentationReady(
  payload: { editionId: string; editionName: string },
  rootStore: RootStore
): void {
  const { editionId, editionName } = payload;
  const { langStore } = rootStore;

  const title = langStore.editionDocumentationReadyTitle;
  const message = langStore.editionDocumentationReadyMessage?.replace('{name}', editionName);

  rootStore.notificationStore.addNotification({
    type: 'success',
    title,
    message,
    data: { editionId },
  });

  rootStore.showSnackbar(message, 'success');

  // Recargar el store si está abierto
  const storePath = `/edition/update/${editionId}`;
  const store = rootStore.storeRegistry.getStoreIfExists(storePath);
  if (store && 'loadEditionById' in store) {
    (store as any).loadEditionById(editionId);
  }
}

/**
 * Handler para cuando falla la generación de documentación.
 */
function handleEditionDocumentationFailed(
  payload: {
    editionId: string;
    editionName: string;
    error: {
      message: string;
      code?: string;
      validationErrors?: Array<{ field: string; message: string }>;
      suggestedAction?: string;
    };
  },
  rootStore: RootStore
): void {
  const { editionId, editionName, error } = payload;
  const { langStore } = rootStore;

  const title = langStore.editionDocumentationFailedTitle;
  let message = langStore.editionDocumentationFailedMessage?.replace('{name}', editionName) || '';

  // Agregar detalles del error
  if (error.validationErrors && error.validationErrors.length > 0) {
    const details = error.validationErrors
      .map((ve) => `${ve.field}: ${ve.message}`)
      .join('; ');
    message = `${message}: ${details}`;
  } else if (error.message) {
    message = `${message}: ${error.message}`;
  }

  if (error.suggestedAction) {
    message = `${message}. ${error.suggestedAction}`;
  }

  rootStore.notificationStore.addNotification({
    type: 'error',
    title,
    message,
    data: { editionId, error },
  });

  rootStore.showSnackbar(message, 'error');

  // Recargar el store si está abierto para mostrar el nuevo estado
  const storePath = `/edition/update/${editionId}`;
  const store = rootStore.storeRegistry.getStoreIfExists(storePath);
  if (store && 'loadEditionById' in store) {
    (store as any).loadEditionById(editionId);
  }
}

/**
 * Handler para cuando el ejemplar de edición está listo.
 */
function handleEditionEjemplarReady(
  payload: { editionId: string; editionName: string },
  rootStore: RootStore
): void {
  const { editionId, editionName } = payload;
  const { langStore } = rootStore;

  const title = langStore.editionEjemplarReadyTitle;
  const message = langStore.editionEjemplarReadyMessage?.replace('{name}', editionName);

  rootStore.notificationStore.addNotification({
    type: 'success',
    title,
    message,
    data: { editionId },
  });

  rootStore.showSnackbar(message, 'success');

  // Recargar el store si está abierto
  const storePath = `/edition/update/${editionId}`;
  const store = rootStore.storeRegistry.getStoreIfExists(storePath);
  if (store && 'loadEditionById' in store) {
    (store as any).loadEditionById(editionId);
  }
}

/**
 * Handler para cuando las firmas fueron enviadas exitosamente.
 */
function handleEditionSignatureSent(
  payload: { editionId: string; editionName: string; sentCount: number },
  rootStore: RootStore
): void {
  const { editionId, editionName, sentCount } = payload;

  const title = 'Firmas enviadas';
  const message = `La documentación de "${editionName}" fue enviada a firmar (${sentCount} documento${sentCount !== 1 ? 's' : ''}).`;

  rootStore.notificationStore.addNotification({
    type: 'success',
    title,
    message,
    data: { editionId },
  });

  rootStore.showSnackbar(message, 'success');

  // Recargar el store si está abierto
  const storePath = `/edition/update/${editionId}`;
  const store = rootStore.storeRegistry.getStoreIfExists(storePath);
  if (store && 'loadEditionById' in store) {
    (store as any).loadEditionById(editionId);
  }
}

/**
 * Handler para cuando todas las firmas fueron completadas.
 */
function handleEditionSignatureCompleted(
  payload: { editionId: string; signedEditionId?: string; editionName: string },
  rootStore: RootStore
): void {
  const { editionId, signedEditionId, editionName } = payload;

  const title = 'Documentación firmada';
  const message = `Toda la documentación de "${editionName}" fue firmada exitosamente.`;

  rootStore.notificationStore.addNotification({
    type: 'success',
    title,
    message,
    data: { editionId, signedEditionId },
  });

  rootStore.showSnackbar(message, 'success');

  // Al firmar todos, el server hace snapshot → SignedEdition y BORRA la Edition
  // viva. Si el form de update de esta Edition está abierto, navegar a la
  // SignedEdition: recargar la Edition borrada daría NOT_FOUND.
  const storePath = `/edition/update/${editionId}`;
  const store = rootStore.storeRegistry.getStoreIfExists(storePath);
  if (store) {
    const target = signedEditionId
      ? `/signedEdition/view/${signedEditionId}`
      : '/signedEdition';
    rootStore.router.goTo(target, undefined, undefined, undefined, 'Edición Firmada');
  }
}

/**
 * Handler para cuando un firmante firma un documento.
 * Refresca los datos de la edición para actualizar el estado de firmas.
 */
function handleEditionRecipientSigned(
  payload: { editionId: string; recipientEmail: string },
  rootStore: RootStore
): void {
  const { editionId } = payload;

  // Recargar el store si está abierto (refresca la tabla de estado de firmas)
  const storePath = `/edition/update/${editionId}`;
  const store = rootStore.storeRegistry.getStoreIfExists(storePath);
  if (store && 'loadSignatureStatuses' in store) {
    (store as any).loadSignatureStatuses(editionId);
  }
}

/**
 * Handler para cuando un email de firma rebota (dirección inválida, buzón lleno, etc.).
 * Muestra notificación de error y refresca el estado de firmas.
 */
function handleEditionSignatureBounced(
  payload: {
    editionId: string;
    editionName: string;
    recipientEmail: string;
    recipientName: string;
  },
  rootStore: RootStore
): void {
  const { editionId, editionName, recipientEmail, recipientName } = payload;

  // Un rebote no es un fallo del sistema: el envío funcionó, pero la casilla del
  // destinatario rechazó el email. Es accionable (corregir el correo y reenviar),
  // por eso 'warning' y no 'error', con la acción a seguir explícita.
  const title = 'No se pudo entregar el email de firma';
  const message =
    `${recipientName} (${recipientEmail}) no recibió el email de "${editionName}". ` +
    `Verificá que la dirección sea correcta y reenviá la solicitud de firma.`;

  rootStore.notificationStore.addNotification({
    type: 'warning',
    title,
    message,
    data: { editionId },
  });

  rootStore.showSnackbar(message, 'warning');

  const storePath = `/edition/update/${editionId}`;
  const store = rootStore.storeRegistry.getStoreIfExists(storePath);
  if (store && 'loadSignatureStatuses' in store) {
    (store as any).loadSignatureStatuses(editionId);
  }
}

/**
 * Registra todos los handlers SSE de Edition.
 * Debe llamarse durante la inicialización de la aplicación.
 */
export function registerEditionSSEHandlers(rootStore: RootStore): void {
  rootStore.eventSourceStore.registerHandler(
    'edition.documentation.ready',
    handleEditionDocumentationReady
  );

  rootStore.eventSourceStore.registerHandler(
    'edition.documentation.failed',
    handleEditionDocumentationFailed
  );

  rootStore.eventSourceStore.registerHandler(
    'edition.ejemplar.ready',
    handleEditionEjemplarReady
  );

  rootStore.eventSourceStore.registerHandler(
    'edition.signature.sent',
    handleEditionSignatureSent
  );

  rootStore.eventSourceStore.registerHandler(
    'edition.signature.completed',
    handleEditionSignatureCompleted
  );

  rootStore.eventSourceStore.registerHandler(
    'edition.recipient.signed',
    handleEditionRecipientSigned
  );

  rootStore.eventSourceStore.registerHandler(
    'edition.signature.bounced',
    handleEditionSignatureBounced
  );

  // Resincronización tras una desconexión SSE: los eventos emitidos mientras
  // la conexión estuvo caída se PERDIERON (SSE no tiene replay). Si hay un
  // form de edición abierto, recargarlo — si la edición ya se movió a
  // SignedEdition (firmas completadas durante la desconexión), el propio
  // loadEditionById lo detecta y navega.
  rootStore.eventSourceStore.registerReconnectHandler((rs) => {
    const match = window.location.pathname.match(/\/edition\/update\/([0-9a-fA-F-]+)/);
    if (!match) return;
    const editionId = match[1];
    const store = rs.storeRegistry.getStoreIfExists(`/edition/update/${editionId}`);
    if (store && 'loadEditionById' in store) {
      (store as any).loadEditionById(editionId);
    }
  });
}
